import { useOrgContext } from '@/org/contexts/OrgContext'
import { GetSearchResultsQuery, useGetSearchResultsLazyQuery } from '@gql'
import { truthy } from '@rolebase/shared/helpers/truthy'
import { SearchHit, SearchTypes } from '@rolebase/shared/model/search'
import { UserLocalStorageKeys } from '@utils/localStorage'
import debounce from 'lodash.debounce'
import { useEffect, useMemo, useState } from 'react'
import { trpc } from 'src/trpc'
import { SearchItem } from '../searchTypes'

// CONF-02, phase 4: the browser used to keep an Algolia key per organisation in
// localStorage. The key no longer exists client-side; drop what is left of it.
function purgeLegacyAlgoliaConfig() {
  try {
    const pattern = new RegExp(
      '^' + UserLocalStorageKeys.AlgoliaConfig.replace('{id}', '.*') + '$'
    )
    for (const key of Object.keys(localStorage)) {
      if (pattern.test(key)) localStorage.removeItem(key)
    }
  } catch {
    // Storage unavailable (private window, blocked site data): nothing to purge.
  }
}

export function useAlgoliaSearch() {
  const { orgId } = useOrgContext()
  const [getSearchResults] = useGetSearchResultsLazyQuery()
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<SearchItem[]>([])

  useEffect(purgeLegacyAlgoliaConfig, [])

  const search = useMemo(() => {
    return debounce(async (value: string, type?: SearchTypes) => {
      if (!value || !orgId) {
        setItems([])
        return
      }

      try {
        setLoading(true)

        // Served by the backend, which re-reads the hits as the current user:
        // only what they can open comes back, as identifiers (CONF-02).
        const hits = await trpc.search.find.query({ orgId, value, type })

        // Get results data from database
        const { data, error } = await getSearchResults({
          variables: {
            membersIds: getHitsIds(hits, SearchTypes.Member),
            circlesIds: getHitsIds(hits, SearchTypes.Circle),
            threadsIds: getHitsIds(hits, SearchTypes.Thread),
            meetingsIds: getHitsIds(hits, SearchTypes.Meeting),
            tasksIds: getHitsIds(hits, SearchTypes.Task),
            decisionsIds: getHitsIds(hits, SearchTypes.Decision),
          },
        })

        if (!data) {
          throw error || new Error('No data')
        }

        // Build search items data with hots order
        const resultsItems = hits
          .map((hit) => getResultByHit(hit, data))
          .filter(truthy)

        setItems(resultsItems)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }, 200)
  }, [orgId])

  return {
    loading,
    items,
    search,
  }
}

function getHitsIds(hits: SearchHit[], type: SearchTypes) {
  return hits.filter((hit) => hit.type === type).map((hit) => hit.objectID)
}

function getResultByHit(
  hit: SearchHit,
  data: GetSearchResultsQuery
): SearchItem | undefined {
  switch (hit.type) {
    case SearchTypes.Circle: {
      const circle = data.circle.find((circle) => circle.id === hit.objectID)
      return (
        circle && {
          id: hit.objectID,
          type: hit.type,
          text: circle.role.name,
          title: circle.role.name,
        }
      )
    }

    case SearchTypes.Member: {
      const member = data.member.find((member) => member.id === hit.objectID)
      return (
        member && {
          id: hit.objectID,
          type: hit.type,
          text: member.name,
          title: member.name,
          picture: member.picture || undefined,
        }
      )
    }

    case SearchTypes.Thread: {
      const thread = data.thread.find((thread) => thread.id === hit.objectID)
      return (
        thread && {
          id: hit.objectID,
          type: hit.type,
          text: thread.title,
          title: thread.title,
          circleId: thread.circleId,
          date: thread.createdAt,
        }
      )
    }

    case SearchTypes.Meeting: {
      const meeting = data.meeting.find(
        (meeting) => meeting.id === hit.objectID
      )
      return (
        meeting && {
          id: hit.objectID,
          type: hit.type,
          text: meeting.title,
          title: meeting.title,
          circleId: meeting.circleId,
          date: meeting.startDate,
        }
      )
    }

    case SearchTypes.Task: {
      const task = data.task.find((task) => task.id === hit.objectID)
      return (
        task && {
          id: hit.objectID,
          type: hit.type,
          text: task.title,
          title: task.title,
          circleId: task.circleId,
          date: task.dueDate || undefined,
        }
      )
    }
    case SearchTypes.Decision: {
      const decision = data.decision.find(
        (decision) => decision.id === hit.objectID
      )
      return (
        decision && {
          id: hit.objectID,
          type: hit.type,
          text: decision.title,
          title: decision.title,
          date: decision.createdAt,
        }
      )
    }
  }
}
